import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs';
import { NbToastrService, NbDialogService } from '@nebular/theme';
import { NbTokenService } from '@nebular/auth';
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';

import {
  GoalData,
  GoalView,
} from '../../../../@core/interfaces/common/CalorieFriend/goals';

import {
  Patient,
  PatientData,
} from '../../../../@core/interfaces/common/CalorieFriend/patients';
import {
  EMAIL_PATTERN,
  PHONE_PATTERN,
} from '../../../../@auth/components/constants';
import { Utility } from '../../../../@core/utils/utility';
import {
  CountryData,
  Country,
} from '../../../../@core/interfaces/common/countries';
import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import { PoundsAndKgComponent } from '../../../../../app/@components/pounds-and-kg/pounds-and-kg.component';
import { GridData } from 'app/@core/interfaces/common/grid-data';
import { PatientSubscriptionMeta } from 'app/@core/interfaces/common/CalorieFriend/patient-subscription';
import { PatientSubscriptionsMetaService } from 'app/@core/backend/common/services/CalorieFriend/patient-subscriptions-meta.service';
import { ConversionFactorsData } from 'app/@core/utils/conversionFactors.data';

/**
 * Component for Patient's general information on the Patient Sheet View
 */
@Component({
  selector: 'app-patient-general-info',
  templateUrl: './patient-general-info.component.html',
  styleUrls: [
    './patient-general-info.component.scss',
    '../../global.component.scss',
  ],
})
export class PatientGeneralInfoComponent
  implements OnInit {
  PatientForm: FormGroup;

  ConversionFactorsPreference = ConversionFactorsData;

  AddPaitentLabel = 'Add New Patient';
  UpdatePaitentLabel = 'Update Patient';
  PaitentLabel = '';

  SavePatientLabel = 'Save Client General Info';
  GeneralInfoLabel = 'General Info';
  FirstNameLabel = 'First Name';
  NameLabel = 'Name';
  LastNameLabel = 'Last Name';
  EmailLabel = 'Email Address';
  BirthDateLabel = 'Date of Birth';
  AddressInfoLabel = 'Address';
  AddressLine1Label = 'Line 1';
  AddressLine2Label = 'Line 2';
  CountryInfoLabel = 'Country';
  PostalCodeInfoLabel = 'Zip / Postal Code';
  PhoneInfoLabel = 'Phone Number';

  BiometricInfoLabel = 'BioMetric Info';
  PreferencesLabel = 'Preferences';
  PreferredUnitLabel = 'Preferred Units';
  HeightLabel = 'Height';
  WeightLabel = 'Weight';
  BodyFatLabel = 'Body Fat %';
  SourceLabel = 'Source';
  BMILabel = 'BMI';
  MeasureDateLabel = 'Date of Measure';

  Updated = 'Updated';
  ProblemCeatingPatient = 'A problem occurred creating the patient.';

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.Canada,
  ];

  /**
   * Input Property which identifies the patient ID (number format)
   */
  @Input('PatientId') PatientId: number = -1;

  /**
   * Input Property which identifies the patient NotificationToken (GUID format)
   */
  @Input('NotificationToken') NotificationToken: string = null;

  /**
   * Input Property which identifies if it is for View or Edit
   */
  @Input('IsView') IsView: boolean = false;

  protected readonly unsubscribe$ = new Subject<void>();

  GoalListSource: Observable<GridData<GoalView>> = null;
  Goals: Array<any> = null;
  public isLoading: boolean = false;

  /** Patient ctor */
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private patientsService: PatientData,
    private tokenService: NbTokenService,
    private toasterService: NbToastrService,
    private countriesService: CountryData,
    private dialogService: NbDialogService,
    private goalsService: GoalData,
    private patientSubscriptionsMetaService: PatientSubscriptionsMetaService
  ) {
    //super(dialogService);
  }

  get Id() {
    return this.PatientForm.get('Id');
  }
  get FirstName() {
    return this.PatientForm.get('FirstName');
  }
  get LastName() {
    return this.PatientForm.get('LastName');
  }
  get EMail() {
    return this.PatientForm.get('EMail');
  }
  get DateOfBirth() {
    return this.PatientForm.get('DateOfBirth');
  }
  get AddressLine1() {
    return this.PatientForm.get('AddressLine1');
  }
  get AddressLine2() {
    return this.PatientForm.get('AddressLine2');
  }
  get Country() {
    return this.PatientForm.get('Country');
  }
  get ZipPostalCode() {
    return this.PatientForm.get('ZipPostalCode');
  }
  get PhoneNumber() {
    return this.PatientForm.get('PhoneNumber');
  }

  get Height() {
    return this.PatientForm.get('Height');
  }
  get Weight() {
    return this.PatientForm.get('Weight');
  }
  get BodyFat() {
    return this.PatientForm.get('BodyFat');
  }
  get Source() {
    return this.PatientForm.get('Source');
  }
  get DateOfMeasure() {
    return this.PatientForm.get('DateOfMeasure');
  }
  get IsPreferredUnitImperial() {
    return this.PatientForm.get('IsPreferredUnitImperial');
  }
  get genderFemale() {
    return this.PatientForm.get('genderFemale');
  }

  get SubscriptionId() {
    return this.PatientForm.get('SubscriptionId');
  }
  get BMI() {
    if (!this.Height.value || this.Height.value === 0) return 'N/A';
    return (
      (this.Weight.value / (this.Height.value * this.Height.value)) *
      10000
    ).toFixed(1);
  }

  Countries: Observable<Country[]>;
  subscriptionData: GridData<PatientSubscriptionMeta>;

  /**
   * Initializes the component.
   * It creates the form to submit the general information of the patient
   * and loads the patient data.
   */
  ngOnInit(): void {
    this.InitPatientForm();
    this.patientSubscriptionsMetaService.list({}).subscribe((data: GridData<PatientSubscriptionMeta>) => {
      this.subscriptionData = data;
    });
    let id = this.route.snapshot.paramMap.get('PatientId');

    if (
      (id == null || id === undefined) &&
      this.PatientId != null &&
      this.PatientId !== undefined &&
      this.PatientId > 0
    )
      id = this.PatientId.toString();

    if (this.NotificationToken == null || this.NotificationToken === undefined)
      this.NotificationToken =
        this.route.snapshot.paramMap.get('NotificationToken');

    if (
      (id !== null && id !== undefined) ||
      (this.NotificationToken != null && this.NotificationToken !== undefined)
    ) {
      this.loadPatientData(id);
      this.PaitentLabel = this.UpdatePaitentLabel;
    } else {
      this.PaitentLabel = this.AddPaitentLabel;
    }

    // load countries
    this.Countries = this.countriesService.list();

    // load goals
    this.GoalListSource = this.goalsService.GoalsList(
      +id,
      this.NotificationToken,
    );
    this.refreshGoals();

    //super.resetDirty();
  }

  refreshGoals() {
    this.GoalListSource.subscribe((data) => {
      this.Goals = data.items;
      //super.resetDirty();
    });
  }

  /**
   * Initializes the Patient Form.
   */
  InitPatientForm() {
    this.PatientForm = this.fb.group({
      Id: this.fb.control(''),
      FirstName: this.fb.control('', [
        Validators.required,
        Validators.minLength(0),
        Validators.maxLength(50),
      ]),
      LastName: this.fb.control('', [
        Validators.required,
        Validators.minLength(0),
        Validators.maxLength(50),
      ]),
      EMail: this.fb.control('', [
        Validators.required,
        Validators.pattern(EMAIL_PATTERN),
      ]),
      DateOfBirth: this.fb.control(new Date(new Date().setHours(0, 0, 0, 0)), [
        Validators.required,
      ]),
      AddressLine1: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(1024),
      ]),
      AddressLine2: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(1024),
      ]),
      Country: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(1024),
      ]),
      ZipPostalCode: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(25),
      ]),
      PhoneNumber: this.fb.control(''),
      Height: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(15),
      ]),
      Weight: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(15),
      ]),
      BodyFat: this.fb.control('', [
        Validators.required,
        Validators.min(0),
        Validators.max(100),
        Validators.pattern('^-?[0-9]*.?[0-9]{0,1}$'),
      ]),
      // Source: this.fb.control('Clinical', [
      //   Validators.required,
      //   Validators.minLength(0),
      //   Validators.maxLength(25),
      // ]),
      // DateOfMeasure: this.fb.control(
      //   new Date(new Date().setHours(0, 0, 0, 0)),
      //   [Validators.required],
      // ),
      IsPreferredUnitImperial: this.fb.control(false),
      genderFemale: this.fb.control(false),
      conversionFactorPreference: this.fb.control(null),
      checkInRequest: this.fb.control(true),
      // SubscriptionId: this.fb.control(0)
    });

    //super.initForm(this.PatientForm);
  }

  /**
   * Converts the object given as a parameter to the 'Patient' type
   * @param value Object to convert into 'Patient' type
   */
  ConvertToPatient(value: any): Patient {
    console.log(value);
    const patient: Patient = {
      ...value,
      PhoneNumber: (value.PhoneNumber ? value.PhoneNumber.internationalNumber : ''),
    };
    return patient;
  }

  /**
   * It converts the inputs of the form into the Patient type
   * and submit the form to save them
   */
  SavePatient() {
    if (this.IsView) return;

    const patient: Patient = this.ConvertToPatient(this.PatientForm.value);

    patient.id = this.PatientId;
    patient.updateBiometrics = true;
    patient.updateGeneralInfo = true;
    this.isLoading = true;

    const observable =
      patient.id === null
        ? this.patientsService.create(patient)
        : this.patientsService.update(patient, this.NotificationToken);

    //super.resetDirty();

    observable.pipe(takeUntil(this.unsubscribe$),
    finalize(() => this.isLoading = false)
    ).subscribe(
      () => {
        this.patientsService.setPatientUpdated(this.PatientId);
        this.handleSuccessResponse();
      },
      (err) => {
        this.handleWrongResponse();
      },
    );

    for (const goal of this.Goals) {
      if (goal.goalType.toLowerCase() === 'weight') {
        const SaveGoal = {
          GoalID: '',
          GoalName: goal.goalName,
          GoalType: 2,
          GoalValue: goal.goalValue,
          StartValue: goal.startValue,
          id: goal.id,
          ID: goal.id,
          PatientID: goal.patientID,
          targetDate: goal.targetDate,
        };

        this.goalsService
          .update(SaveGoal)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            () => {},
            (err) => {
              const t = 0;
            },
          );
      }
    }
  }

  /**
   * Method to navigate to the patients list page
   */
  Back() {
    this.router.navigate(['/pages/CalorieFriend/Patients']);
  }

  /**
   * Handler of Success event of submit
   */
  handleSuccessResponse() {
    this.toasterService.success('', `Item ${this.Updated}!`);
  }

  /**
   * Handler of Fail event of submit
   */
  handleWrongResponse() {
    this.toasterService.danger('', this.ProblemCeatingPatient);
  }

  /**
   * Loads the data of the Patient given by {id}
   * @param id The identifier of the Patient
   */
  loadPatientData(id) {
    if (
      (id != null && id !== undefined) ||
      (this.NotificationToken != null && this.NotificationToken !== undefined)
    ) {
      this.isLoading = true;
      this.patientsService
        .get(id, this.NotificationToken)
        .pipe(
          takeUntil(this.unsubscribe$),
          finalize(() => this.isLoading = false)
        )
        .subscribe((patient) => {
          this.PatientId = patient.id;

          const measureDateUtc = new Date(patient.dateOfMeasure);
          const measureDateLocal = new Date(
            measureDateUtc.getTime() -
              measureDateUtc.getTimezoneOffset() * 60000,
          );

          const birthDateUtc = new Date(patient.dateOfBirth);
          const birthDateLocal = new Date(
            birthDateUtc.getTime() - birthDateUtc.getTimezoneOffset() * 60000,
          );

          this.PatientForm.patchValue({
            Id: patient.id,
            FirstName: patient.firstName,
            LastName: patient.lastName,
            EMail: patient.eMail,
            DateOfBirth: birthDateLocal,
            AddressLine1: patient.addressLine1,
            AddressLine2: patient.addressLine2,
            Country: patient.country,
            ZipPostalCode: patient.zipPostalCode,
            PhoneNumber: patient.phoneNumber,
            Height: patient.height,
            Weight: patient.weight,
            BodyFat: patient.bodyFat,
            Source: patient.source,
            DateOfMeasure: measureDateLocal,
            IsPreferredUnitImperial: patient.isPreferredUnitImperial,
            genderFemale: patient.genderFemale,
            conversionFactorPreference: patient.conversionFactorPreference,
            checkInRequest: patient.checkInRequest,
          });

          //super.resetDirty();
        });
    }
  }

  /**
   * Validator that validates the number with the ##.# pattern
   */
  validateNumber = Utility.validateNumber;

  ChangeScale($event) {
    return;

    $event.stopImmediatePropagation();

    const IsImperial = !this.IsPreferredUnitImperial.value;

    // patient original weight
    const AdjustWeight = new PoundsAndKgComponent();

    // current weight
    if (IsImperial) AdjustWeight._value = this.Weight.value;
    else AdjustWeight.pounds = this.Weight.value;

    AdjustWeight.updateValues(!IsImperial);

    this.PatientForm.patchValue({
      Weight: IsImperial ? AdjustWeight.pounds : AdjustWeight._value,
    });

    for (const goal of this.Goals) {
      if (goal.goalType.toLowerCase() === 'weight') {
        // current weight
        if (IsImperial) AdjustWeight._value = goal.goalValue;
        else AdjustWeight.pounds = goal.goalValue;

        AdjustWeight.updateValues(!IsImperial);

        if (IsImperial) goal.goalValue = AdjustWeight.pounds;
        else goal.goalValue = AdjustWeight._value;

        // start weight
        if (IsImperial) AdjustWeight._value = goal.startValue;
        else AdjustWeight.pounds = goal.startValue;

        AdjustWeight.updateValues(!IsImperial);

        if (IsImperial) goal.startValue = AdjustWeight.pounds;
        else goal.startValue = AdjustWeight._value;
      }
    }
  }
}
